<div class="relative overflow-hidden text-slate-12">
  <!-- Close Button -->
  <button
    type="button"
    (click)="closeBetaModal()"
    class="absolute top-5 right-5 z-10 flex size-8 items-center justify-center rounded-full bg-neutral-2 text-neutral-11 hover:bg-neutral-3 hover:text-neutral-12 transition-colors cursor-pointer"
    aria-label="Fermer"
  >
    ✕
  </button>

  @if (!isBetaSubmitted()) {
    <div>
      <span
        class="inline-flex items-center gap-x-1.5 rounded-full bg-primary-a3 px-3 py-1 text-xs font-semibold text-primary-11 ring-1 ring-inset ring-primary-a4"
      >
        Bêta privée ETXE
      </span>
      <h3 class="mt-3 text-2xl font-bold tracking-tight text-neutral-12">Rejoignez la bêta privée</h3>
      <p class="mt-2 text-sm text-neutral-11">
        Inscrivez-vous pour bénéficier d'un accès prioritaire lors du lancement officiel au Pays Basque.
      </p>

      <form [formGroup]="formGroup" (ngSubmit)="submitBetaForm()" class="mt-6 flex flex-col gap-4">
        <!-- Role Selector Tabs inside Modal -->
        <div>
          <span class="block text-xs font-bold uppercase tracking-wider text-neutral-11 mb-2">
            Vous êtes :
          </span>
          <div class="grid grid-cols-2 gap-2 bg-neutral-2 p-1 rounded-xl border border-neutral-a3">
            <button
              type="button"
              (click)="setBetaRole('vendeur')"
              class="rounded-lg py-2 text-xs font-bold transition-all duration-200 cursor-pointer"
              [class]="
                formGroup.controls.role.value === 'vendeur'
                  ? 'bg-primary-9 text-white shadow-sm'
                  : 'text-neutral-11 hover:text-neutral-12'
              "
            >
              🏡 Propriétaire vendeur
            </button>
            <button
              type="button"
              (click)="setBetaRole('agent')"
              class="rounded-lg py-2 text-xs font-bold transition-all duration-200 cursor-pointer"
              [class]="
                formGroup.controls.role.value === 'agent'
                  ? 'bg-neutral-12 text-neutral-1 shadow-sm'
                  : 'text-neutral-11 hover:text-neutral-12'
              "
            >
              🏢 Agent, mandataire ou indépendant
            </button>
          </div>
        </div>

        <!-- First & Last Name Grid -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-full">
            <mat-label>Prénom</mat-label>
            <input matInput formControlName="firstName" placeholder="Jean" required />
            @if (formGroup.controls.firstName.hasError('required')) {
              <mat-error>Le prénom est requis</mat-error>
            }
          </mat-form-field>

          <mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-full">
            <mat-label>Nom</mat-label>
            <input matInput formControlName="lastName" placeholder="Dupont" required />
            @if (formGroup.controls.lastName.hasError('required')) {
              <mat-error>Le nom est requis</mat-error>
            }
          </mat-form-field>
        </div>

        <!-- Email & Phone Number Grid -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 pb-2">
          <mat-form-field appearance="outline" class="w-full">
            <mat-label>Adresse e-mail</mat-label>
            <input
              matInput
              type="email"
              formControlName="email"
              placeholder="jean.dupont@exemple.fr"
              required
            />
            @if (formGroup.controls.email.hasError('required')) {
              <mat-error>L'adresse e-mail est requise</mat-error>
            } @else if (formGroup.controls.email.hasError('email')) {
              <mat-error>Format d'e-mail invalide</mat-error>
            }
          </mat-form-field>

          <div>
            <aura-phone-number-input
              [control]="formGroup.controls.mobileNumber"
              [required]="false"
              [onlyCountries]="onlyCountries()"
              [preferredCountries]="preferredCountries()"
              label="Numéro de téléphone (optionnel)"
            />
          </div>
        </div>

        <!-- Role-Specific Additional Questions -->
        @if (formGroup.controls.role.value === 'agent') {
          <div class="space-y-4">
            <mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-full">
              <mat-label>Départements d'exercice (optionnel)</mat-label>
              <mat-select formControlName="agentDepartments" multiple>
                @for (dept of frenchDepartments; track dept.code) {
                  <mat-option [value]="dept.code">{{ dept.label }}</mat-option>
                }
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-full">
              <mat-label>N° de carte T (optionnel)</mat-label>
              <input
                matInput
                formControlName="agentCarteT"
                placeholder="CPI00000000000012345"
                (blur)="formatAndSetCarteT()"
              />
            </mat-form-field>
          </div>
        } @else if (formGroup.controls.role.value === 'vendeur') {
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-full">
              <mat-label>Urgence de la vente (optionnel)</mat-label>
              <mat-select formControlName="sellerSaleUrgency">
                <mat-option value="">-- Aucun --</mat-option>
                @for (opt of saleUrgencyOptions; track opt.value) {
                  <mat-option [value]="opt.value">{{ opt.label }}</mat-option>
                }
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-full">
              <mat-label>Département du bien (optionnel)</mat-label>
              <mat-select formControlName="sellerDepartment">
                <mat-option value="">-- Aucun --</mat-option>
                @for (dept of frenchDepartments; track dept.code) {
                  <mat-option [value]="dept.code">{{ dept.label }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
          </div>
        }

        @if (betaErrorMessage()) {
          <div class="rounded-xl bg-red-a3 p-3 border border-red-a4 text-xs font-semibold text-red-11 mt-2">
            {{ betaErrorMessage() }}
          </div>
        }

        <!-- Submit Button -->
        <button
          type="submit"
          [disabled]="isBetaSubmitting() || formGroup.invalid"
          class="w-full rounded-xl bg-primary-9 py-3.5 px-4 text-sm font-bold text-white shadow-md hover:bg-primary-10 transition-all duration-200 cursor-pointer mt-4 flex items-center justify-center gap-x-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary-9"
        >
          @if (isBetaSubmitting()) {
            <span>Enregistrement...</span>
          } @else {
            <span>Demander mon accès prioritaire →</span>
          }
        </button>
      </form>
    </div>
  } @else {
    <!-- Confirmation & Verification Flow -->
    @if (isEmailVerified()) {
      <!-- Final Success Screen (Only after email code is verified) -->
      <div class="text-center py-6 space-y-5">
        <div
          class="flex size-14 items-center justify-center rounded-full bg-green-a3 text-green-9 font-bold text-2xl mx-auto border border-green-a4"
        >
          ✓
        </div>
        <h3 class="text-2xl font-bold text-neutral-12">Demande enregistrée !</h3>
        <p class="text-sm text-neutral-11 leading-relaxed max-w-sm mx-auto">
          Merci <strong class="text-neutral-12">{{ formGroup.controls.firstName.value }}</strong
          >. Votre inscription en tant que
          <strong>{{
            formGroup.controls.role.value === 'vendeur'
              ? 'Propriétaire vendeur'
              : 'agent, mandataire ou indépendant'
          }}</strong>
          a été enregistrée avec succès.
          <br />
          <br />
          <strong>Nous estimons que la bêta sera ouverte au premier trimestre 2027.</strong>
        </p>

        <div
          class="rounded-xl bg-green-a3 p-3.5 border border-green-a4 flex items-center justify-center gap-x-2.5 text-xs font-bold text-green-11 max-w-sm mx-auto"
        >
          <span>✅</span>
          <span>{{
            verificationFeedbackMessage() || 'Votre adresse e-mail a été vérifiée avec succès !'
          }}</span>
        </div>

        <div class="pt-2">
          <button
            type="button"
            (click)="closeBetaModal()"
            class="rounded-xl bg-neutral-12 px-6 py-2.5 text-xs font-bold text-neutral-1 hover:bg-neutral-11 transition-colors cursor-pointer"
          >
            Fermer la fenêtre
          </button>
        </div>
      </div>
    } @else {
      <!-- Verification Step (Code entry first) -->
      <div class="py-4 space-y-5">
        <div class="text-center space-y-2">
          <span
            class="inline-flex items-center gap-x-1.5 rounded-full bg-primary-a3 px-3 py-1 text-xs font-semibold text-primary-11 ring-1 ring-inset ring-primary-a4"
          >
            Étape 2 / 2 : Confirmation par e-mail
          </span>
          <h3 class="text-2xl font-bold tracking-tight text-neutral-12">Validez votre adresse e-mail</h3>
          <p class="text-sm text-neutral-11 leading-relaxed max-w-sm mx-auto">
            Un code de vérification à 6 chiffres vous a été envoyé à l'adresse<br />
            <strong class="text-neutral-12">{{ formGroup.controls.email.value }}</strong>
          </p>
        </div>

        <div class="rounded-2xl bg-neutral-2 p-5 border border-neutral-a3 space-y-4">
          <label
            for="verification-code"
            class="block text-xs font-bold uppercase tracking-wider text-neutral-11"
          >
            Code de vérification (6 chiffres) :
          </label>

          <div class="flex items-center gap-2">
            <input
              id="verification-code"
              type="text"
              maxlength="6"
              placeholder="123456"
              [value]="verificationTokenInput()"
              (input)="updateVerificationTokenInput($event)"
              class="flex-1 rounded-xl border border-neutral-a3 bg-neutral-1 px-4 py-3 text-center text-lg font-mono tracking-widest text-neutral-12 focus:border-primary-9 focus:outline-none focus:ring-1 focus:ring-primary-9"
            />
            <button
              type="button"
              (click)="submitVerificationCode()"
              [disabled]="isVerifyingCode() || !verificationTokenInput().trim()"
              class="rounded-xl bg-primary-9 px-5 py-3 text-sm font-bold text-white shadow-md hover:bg-primary-10 transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              @if (isVerifyingCode()) {
                <span>Vérification...</span>
              } @else {
                <span>Confirmer</span>
              }
            </button>
          </div>

          @if (verificationFeedbackMessage()) {
            <p
              class="text-xs font-medium text-amber-11 bg-amber-a3 p-3 rounded-xl border border-amber-a4 text-center"
            >
              {{ verificationFeedbackMessage() }}
            </p>
          }
        </div>

        <div class="text-center pt-2">
          <button
            type="button"
            (click)="closeBetaModal()"
            class="text-xs text-neutral-11 hover:text-neutral-12 font-medium transition-colors cursor-pointer"
          >
            Fermer sans valider maintenant
          </button>
        </div>
      </div>
    }
  }
</div>
